// ReliabilityContinuity.jsx
import { useEffect, useRef } from "react";
import { motion, useInView, useAnimation } from "framer-motion";

const ReliabilityContinuity = () => {
  const controls = useAnimation();
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, threshold: 0.1 });

  useEffect(() => {
    if (isInView) {
      controls.start("visible");
    }
  }, [controls, isInView]);

  const features = [
    {
      title: "Automatic Backups",
      description: "Daily copies of your data stored safely.",
    },
    {
      title: "Multiple Locations",
      description: "Data stored in different places for extra safety.",
    },
    {
      title: "Quick Recovery",
      description: "Get your workback fast if anything goes wrong.",
    },
  ];

  const metrics = [
    { label: "System Uptime", value: 95, target: 95 },
    { label: "Data Redundancy", value: 88, target: 88 },
    { label: "Recovery Readiness", value: 92, target: 92 },
  ];

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.15,
        delayChildren: 0.3,
      },
    },
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 30 },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        type: "spring",
        damping: 12,
        stiffness: 100,
        duration: 0.6,
      },
    },
  };

  const featureVariants = {
    hidden: { opacity: 0, x: -30 },
    visible: {
      opacity: 1,
      x: 0,
      transition: {
        type: "spring",
        damping: 12,
        stiffness: 100,
      },
    },
  };

  const rightPanelVariants = {
    hidden: { opacity: 0, x: 50, scale: 0.95 },
    visible: {
      opacity: 1,
      x: 0,
      scale: 1,
      transition: {
        type: "spring",
        damping: 15,
        stiffness: 80,
        duration: 0.6,
        delay: 0.2,
      },
    },
  };

  // Animation for the 99.9% - coming out and going inside like a pulse/scale effect
  const percentageVariants = {
    hidden: { opacity: 0, scale: 0.5 },
    visible: {
      opacity: 1,
      scale: [0.5, 1.2, 1],
      transition: {
        duration: 0.8,
        ease: "easeOut",
        times: [0, 0.5, 1],
      },
    },
  };

  return (
    <section className="ReliabilityContinuity px-6 py-20 lg:py-28 bg-gradient-to-b from-[#093A5B]/[0.04] via-white to-white relative overflow-hidden">
      {/* Animated Wave Background */}
      <svg
        className="absolute inset-0 w-full h-full opacity-[0.03]"
        xmlns="http://www.w3.org/2000/svg"
      >
        <defs>
          <pattern
            id="wave"
            x="0"
            y="0"
            width="100"
            height="100"
            patternUnits="userSpaceOnUse"
          >
            <motion.path
              d="M 0 50 Q 25 25.00072, 50 50 T 100 50"
              fill="none"
              stroke="#093A5B"
              strokeWidth="1"
              animate={{
                d: [
                  "M 0 50 Q 25 25.00072, 50 50 T 100 50",
                  "M 0 50 Q 25 75.00072, 50 50 T 100 50",
                  "M 0 50 Q 25 25.00072, 50 50 T 100 50",
                ],
              }}
              transition={{
                duration: 4,
                repeat: Infinity,
                ease: "easeInOut",
              }}
            />
          </pattern>
        </defs>
        <rect width="100%" height="100%" fill="url(#wave)" />
      </svg>

      {/* Floating Particles */}
      <div className="absolute inset-0 pointer-events-none">
        {[...Array(25)].map((_, i) => (
          <motion.div
            key={i}
            className="absolute w-0.5 h-0.5 bg-[#093A5B]/20 rounded-full"
            initial={{
              x: Math.random() * window.innerWidth,
              y: Math.random() * window.innerHeight,
              opacity: 0,
            }}
            animate={{
              y: [0, -80, 0],
              opacity: [0, 0.4, 0],
            }}
            transition={{
              duration: 4 + Math.random() * 3,
              repeat: Infinity,
              delay: Math.random() * 5,
              ease: "easeInOut",
            }}
            style={{
              left: `${Math.random() * 100}%`,
              top: `${Math.random() * 100}%`,
            }}
          />
        ))}
      </div>

      <div className="mx-auto max-w-7xl relative z-10" ref={ref}>
        <div className="grid lg:grid-cols-2 gap-12 lg:gap-16 items-center">
          {/* Left Content */}
          <motion.div
            initial={{ opacity: 0, x: -50 }}
            animate={controls}
            variants={{
              visible: { opacity: 1, x: 0, transition: { duration: 0.6 } },
            }}
          >
            {/* Heading */}
            <motion.h2
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.2, duration: 0.6 }}
              className="text-4xl lg:text-5xl font-bold text-black mb-6 leading-tight"
            >
              Always available when you need it
            </motion.h2>

            {/* Description */}
            <motion.p
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.3, duration: 0.6 }}
              className="text-lg text-gray-600 leading-relaxed mb-6"
            >
              For busy teams, security also means reliability. Your work needs
              to be there when you need it. Simplifyte is designed to stay up
              and running with safe storage and smart backup systems.
            </motion.p>

            {/* Features List */}
            <motion.div
              variants={containerVariants}
              initial="hidden"
              animate={controls}
              className="space-y-4"
            >
              {features.map((feature, index) => (
                <motion.div
                  key={index}
                  variants={featureVariants}
                  whileHover={{ x: 8, y: -2 }}
                  className="flex items-start gap-3 bg-white/50 backdrop-blur-sm rounded-2xl p-4 border border-[#093A5B]/10 hover:border-[#093A5B]/20 hover:shadow-lg transition-all duration-300 group"
                >
                  <motion.div
                    className="w-6 h-6 rounded-lg bg-gradient-to-br from-[#093A5B]/20 to-[#093A5B]/10 flex items-center justify-center flex-shrink-0 mt-1 group-hover:from-[#093A5B]/30 group-hover:to-[#093A5B]/20 transition-all duration-300"
                    whileHover={{ scale: 1.2, rotate: 90 }}
                    transition={{ duration: 0.3 }}
                  >
                    <div className="w-2 h-2 rounded-full bg-[#093A5B]" />
                  </motion.div>
                  <div>
                    <h4 className="font-semibold text-black mb-1 group-hover:text-[#093A5B] transition-colors">
                      {feature.title}
                    </h4>
                    <p className="text-gray-600 text-sm">
                      {feature.description}
                    </p>
                  </div>
                </motion.div>
              ))}
            </motion.div>
          </motion.div>

          {/* Right Panel - Uptime Stats */}
          <motion.div
            variants={rightPanelVariants}
            initial="hidden"
            animate={controls}
            className="relative"
          >
            <div className="relative bg-gradient-to-br from-white to-[#093A5B]/[0.05] rounded-3xl p-12 border-2 border-[#093A5B]/10 shadow-xl overflow-hidden">
              {/* Animated Background Glow */}
              <motion.div
                className="absolute inset-0 bg-gradient-to-r from-[#093A5B]/5 via-transparent to-[#093A5B]/5"
                // animate={{
                //   x: ["-100%", "100%"],
                // }}
                // transition={{
                //   duration: 8,
                //   repeat: Infinity,
                //   ease: "linear",
                // }}
              />

              {/* Uptime Percentage */}
              <div className="flex items-center justify-center mb-8">
                <motion.div
                  className="relative"
                  initial="hidden"
                  animate={controls}
                  variants={{
                    hidden: { scale: 0 },
                    visible: {
                      scale: 1,
                      transition: {
                        type: "spring",
                        damping: 12,
                        stiffness: 100,
                        delay: 0.4,
                      },
                    },
                  }}
                >
                  <motion.div
                    className="text-7xl font-bold text-[#093A5B]"
                    variants={percentageVariants}
                    // initial="hidden"
                    // animate={controls}
                    // initial={{ opacity: 0, y: 20 }}
                    // animate={{ opacity: 1, y: 0 }}
                    // transition={{ delay: 0.5, duration: 0.5 }}
                    // whileHover={{ y: -8, scale: 1.02 }}
                    // transition={{ type: "spring", damping: 20, stiffness: 300 }}
                    animate={{
                      scale: [1, 1.2, 1, 1.05, 1],
                    }}
                    transition={{
                      duration: 1.2,
                      repeat: Infinity,
                      ease: "easeInOut",
                      times: [0, 0.2, 0.4, 0.6, 1],
                    }}
                  >
                    99.9%
                  </motion.div>
                  <motion.div
                    className="absolute inset-0 bg-[#093A5B]/20 rounded-full blur-xl"
                    animate={{
                      scale: [1, 1.2, 1],
                      opacity: [0.3, 0.6, 0.3],
                    }}
                    transition={{
                      duration: 2,
                      repeat: Infinity,
                      ease: "easeInOut",
                    }}
                  />
                </motion.div>
              </div>

              {/* Uptime Text */}
              <div className="text-center mb-8">
                <motion.h3
                  initial={{ opacity: 0, y: 20 }}
                  animate={controls}
                  variants={{
                    hidden: { opacity: 0, y: 20 },
                    visible: {
                      opacity: 1,
                      y: 0,
                      transition: { delay: 0.6, duration: 0.5 },
                    },
                  }}
                  className="text-2xl font-bold text-black mb-2"
                >
                  Uptime Commitment
                </motion.h3>
                <motion.p
                  initial={{ opacity: 0, y: 20 }}
                  animate={controls}
                  variants={{
                    hidden: { opacity: 0, y: 20 },
                    visible: {
                      opacity: 1,
                      y: 0,
                      transition: { delay: 0.7, duration: 0.5 },
                    },
                  }}
                  className="text-gray-600"
                >
                  Always here when your team needs to work
                </motion.p>
              </div>

              {/* Progress Bars */}
              <motion.div
                variants={containerVariants}
                initial="hidden"
                animate={controls}
                className="space-y-3"
              >
                {metrics.map((metric, index) => (
                  <motion.div
                    key={index}
                    variants={itemVariants}
                    className="relative"
                  >
                    <div className="flex justify-between mb-1 text-sm">
                      <span className="text-gray-600">{metric.label}</span>
                      <motion.span
                        className="text-[#093A5B] font-semibold"
                        initial={{ opacity: 0 }}
                        animate={controls}
                        variants={{
                          hidden: { opacity: 0 },
                          visible: {
                            opacity: 1,
                            transition: { delay: 0.8 + index * 0.1 },
                          },
                        }}
                      >
                        {metric.value}%
                      </motion.span>
                    </div>
                    <div className="h-2 bg-[#093A5B]/10 rounded-full overflow-hidden">
                      <motion.div
                        className="h-full bg-gradient-to-r from-[#093A5B] to-[#093A5B]/70 rounded-full"
                        initial={{ width: 0 }}
                        animate={controls}
                        variants={{
                          hidden: { width: 0 },
                          visible: {
                            width: `${metric.value}%`,
                            transition: {
                              duration: 1.2,
                              delay: 0.8 + index * 0.1,
                              ease: "easeOut",
                            },
                          },
                        }}
                      />
                    </div>
                  </motion.div>
                ))}
              </motion.div>

              {/* Animated Bottom Border */}
              <motion.div
                className="absolute bottom-0 left-0 right-0 h-[2px] bg-gradient-to-r from-[#093A5B] via-[#093A5B]/50 to-transparent"
                initial={{ scaleX: 0 }}
                whileHover={{ scaleX: 1 }}
                transition={{ duration: 0.4 }}
                style={{ transformOrigin: "left" }}
              />
            </div>
          </motion.div>
        </div>
      </div>

      {/* Custom CSS Animations */}
      <style jsx>{`
        .ReliabilityContinuity {
          --reliability-primary: #093a5b;
        }

        @keyframes float {
          0%,
          100% {
            transform: translateY(0px) translateX(0px);
          }
          50% {
            transform: translateY(-20px) translateX(10px);
          }
        }

        @keyframes pulse-ring {
          0% {
            transform: scale(0.8);
            opacity: 0.5;
          }
          100% {
            transform: scale(1.2);
            opacity: 0;
          }
        }

        @keyframes shimmer {
          0% {
            transform: translateX(-100%);
          }
          100% {
            transform: translateX(100%);
          }
        }

        @keyframes wave-animation {
          0% {
            transform: translateX(0);
          }
          100% {
            transform: translateX(-100%);
          }
        }

        .animate-float {
          animation: float 6s ease-in-out infinite;
        }

        .animate-pulse-ring {
          animation: pulse-ring 2s cubic-bezier(0.4, 0, 0.6, 1) infinite;
        }

        .animate-shimmer {
          animation: shimmer 2s infinite;
        }

        .animate-wave {
          animation: wave-animation 8s linear infinite;
        }

        /* Hover Effects */
        .group:hover .group-hover\:scale-105 {
          transform: scale(1.05);
        }

        /* Responsive Adjustments */
        @media (max-width: 640px) {
          .ReliabilityContinuity {
            padding-left: 1rem;
            padding-right: 1rem;
          }
        }

        @media (min-width: 768px) and (max-width: 1024px) {
          .ReliabilityContinuity {
            padding-left: 2rem;
            padding-right: 2rem;
          }
        }

        /* Smooth Scrolling */
        html {
          scroll-behavior: smooth;
        }

        /* Custom Scrollbar */
        ::-webkit-scrollbar {
          width: 8px;
          height: 8px;
        }

        ::-webkit-scrollbar-track {
          background: #f1f1f1;
          border-radius: 10px;
        }

        ::-webkit-scrollbar-thumb {
          background: var(--reliability-primary);
          border-radius: 10px;
        }

        ::-webkit-scrollbar-thumb:hover {
          background: #0a4a6e;
        }

        /* Selection Style */
        ::selection {
          background: var(--reliability-primary);
          color: white;
        }

        /* Focus Styles */
        a:focus-visible,
        button:focus-visible {
          outline: 2px solid var(--reliability-primary);
          outline-offset: 2px;
          border-radius: 12px;
        }
      `}</style>
    </section>
  );
};

export default ReliabilityContinuity;
